import { cn } from '@/lib/utils/cn'
import type { CalendarGridCell } from '@/lib/venue/calendarGrid'
import { dayOfWeekIndex } from '@/lib/venue/calendarGrid'
import type { ReservationCalendarDayView } from '@/lib/venue/reservationTypes'
import { DAY_BUCKET_LEGEND, summarizeDayBuckets } from '@/lib/venue/reservationViewModel'

import { SWATCH_CLASS } from './ReservationLegend'

const WEEKDAY_LABELS = ['일', '월', '화', '수', '목', '금', '토']

function DayCounts({ day }: { day: ReservationCalendarDayView }) {
  const buckets = summarizeDayBuckets(day)
  const nonZero = DAY_BUCKET_LEGEND.filter((entry) => buckets[entry.key] > 0)

  if (nonZero.length === 0) return null

  return (
    <ul className="mt-1 space-y-0.5">
      {nonZero.map((entry) => (
        <li key={entry.key} className="flex items-center gap-1 text-[11px] text-slateish-600">
          <span aria-hidden="true" className={cn('h-1.5 w-1.5 shrink-0 rounded-full', SWATCH_CLASS[entry.tone])} />
          {buckets[entry.key]}
        </li>
      ))}
    </ul>
  )
}

/**
 * 캘린더 월/주 그리드(설계 §2-4(1)) — 날짜 셀마다 범례 A(5버킷) 카운트를 보여준다.
 * `CLOSED` 날짜는 시각적으로 흐리게 표시하되 카운트는 그대로 보여준다(관리자는 지난 휴무일
 * 현황도 봐야 한다 — api-spec §1).
 */
export function ReservationCalendarGrid({
  cells,
  days,
  selectedDate,
  onSelectDate,
  showWeekdayHeader = true,
}: {
  cells: CalendarGridCell[]
  days: ReservationCalendarDayView[]
  selectedDate: string
  onSelectDate: (date: string) => void
  showWeekdayHeader?: boolean
}) {
  const dayByDate = new Map(days.map((day) => [day.date, day]))

  return (
    <div className="overflow-hidden rounded-xl border border-slateish-200">
      {showWeekdayHeader ? (
        <div className="grid grid-cols-7 border-b border-slateish-200 bg-slateish-50">
          {WEEKDAY_LABELS.map((label) => (
            <div key={label} className="px-2 py-2 text-center text-xs font-medium text-slateish-600">
              {label}
            </div>
          ))}
        </div>
      ) : null}

      <div className="grid grid-cols-7">
        {cells.map((cell) => {
          const day = dayByDate.get(cell.date)
          const isSelected = cell.date === selectedDate
          const isClosed = day?.state === 'CLOSED' || day?.state === 'NOT_OPERATING'
          const dateNumber = Number(cell.date.slice(-2))
          const weekdayIndex = dayOfWeekIndex(cell.date)

          return (
            <button
              key={cell.date}
              type="button"
              onClick={() => {
                onSelectDate(cell.date)
              }}
              aria-current={isSelected ? 'date' : undefined}
              className={cn(
                'min-h-[84px] border-b border-r border-slateish-200 p-2 text-left align-top last:border-r-0',
                'hover:bg-brand-50/40 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brand-500',
                !cell.inCurrentPeriod && 'bg-slateish-50/60',
                isSelected && 'bg-brand-50',
                isClosed && 'opacity-60'
              )}
            >
              <span
                className={cn(
                  'text-sm font-medium',
                  !cell.inCurrentPeriod ? 'text-slateish-400' : 'text-ink-700',
                  weekdayIndex === 0 && cell.inCurrentPeriod && 'text-danger-500',
                  weekdayIndex === 6 && cell.inCurrentPeriod && 'text-brand-500'
                )}
              >
                {dateNumber}
              </span>
              {day ? <DayCounts day={day} /> : null}
            </button>
          )
        })}
      </div>
    </div>
  )
}
